import { createContext, ReactNode, useState, useMemo, useContext } from 'react';

import { InquisitiveYears } from 'global/graphql/scalars.type';
import {
  AIDictionaryYearRange,
  AI_DICTIONARY_YEAR_RANGE_3_6,
  AI_DICTIONARY_YEAR_RANGE_K_2,
  ActivitiesContentType,
  LANGUAGE_CODE_EN_US,
  LESSON_ACTIVITIES,
  LanguageCodes,
} from 'global/constants';
import { getInteractiveContentLabel } from 'approot/activity-builder/activity-builder.utils';

type FieldError = {
  id: string;
  language: LanguageCodes;
};

type FormErrors = {
  fields: FieldError[];
  time: number;
};

type VocabularySettings = {
  word?: string;
  year?: InquisitiveYears;
  contentType: ActivitiesContentType;
  contentId: number;
  sectionId: string;
  contentLabel: string;
  formErrors?: FormErrors;
  currentLanguage: LanguageCodes;
  setWord: (word: string | undefined) => void;
  setFormErrors: (errors: FieldError[] | undefined) => void;
  setCurrentLanguage: (language: LanguageCodes) => void;
};

export const VocabularySettingContext = createContext<VocabularySettings>({
  word: undefined,
  year: '0',
  contentType: LESSON_ACTIVITIES,
  contentId: 0,
  sectionId: 'abc',
  contentLabel: '',
  formErrors: undefined,
  currentLanguage: LANGUAGE_CODE_EN_US,
  setWord: () => {},
  setFormErrors: () => {},
  setCurrentLanguage: () => {},
});

export const VocabularySettingContextProvider = ({
  children,
  initialYear,
  contentId,
  sectionId,
  contentType,
}: {
  children: ReactNode;
  initialYear?: InquisitiveYears;
  contentId: number;
  sectionId: string;
  contentType: ActivitiesContentType;
}) => {
  const [word, setWord] = useState<string | undefined>();
  const [year, setYear] = useState<InquisitiveYears | undefined>(initialYear);
  const [formErrors, setFormErrors] = useState<FormErrors | undefined>(
    undefined
  );
  const [currentLanguage, setCurrentLanguage] =
    useState<LanguageCodes>(LANGUAGE_CODE_EN_US);

  const value = useMemo(
    () => ({
      word,
      year,
      sectionId,
      contentId,
      contentType,
      contentLabel: getInteractiveContentLabel(contentType),
      formErrors,
      currentLanguage,
      setWord,
      setYear,
      setFormErrors: (errors: FieldError[] | undefined) => {
        if (errors) {
          setFormErrors({
            fields: errors,
            time: Date.now(),
          });
        } else {
          setFormErrors(undefined);
        }
      },
      setCurrentLanguage,
    }),
    [
      word,
      year,
      sectionId,
      contentId,
      contentType,
      formErrors,
      currentLanguage,
      setWord,
      setYear,
      setFormErrors,
      setCurrentLanguage,
    ]
  );

  return (
    <VocabularySettingContext.Provider value={value}>
      {children}
    </VocabularySettingContext.Provider>
  );
};

export const useVocabularySettingContext = (): VocabularySettings & {
  yearGroup: AIDictionaryYearRange;
} => {
  const data = useContext(VocabularySettingContext);
  return {
    ...data,
    yearGroup:
      Number(data.year) <= 2
        ? AI_DICTIONARY_YEAR_RANGE_K_2
        : AI_DICTIONARY_YEAR_RANGE_3_6,
  };
};
